import { useAtom, useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';

import {
  displayDataFamilyFor,
  filtersFamilyFor,
  paginationFamily,
  sortFamilyFor,
} from './state';
import { DataViewKey } from './types';

export const useDataView = <T extends Record<string, unknown>>(
  key: DataViewKey,
) => {
  const filtersFamily = filtersFamilyFor<T>();
  const sortFamily = sortFamilyFor();
  const displayFamily = displayDataFamilyFor<T>();

  const [filters, setFilters] = useAtom(filtersFamily(key));
  const [sort, setSort] = useAtom(sortFamily(key));
  const [pagination, setPagination] = useAtom(paginationFamily(key));

  const resetFilters = useResetAtom(filtersFamily(key));
  const resetSort = useResetAtom(sortFamily(key));
  const resetPagination = useResetAtom(paginationFamily(key));

  const display = useAtomValue(displayFamily(key)); // DisplayState<T>

  const showMore = () => {
    setPagination(p => ({ ...p, pageIndex: p.pageIndex + 1 }));
  };

  const setSearch = (search: string, searchKey?: keyof T | (keyof T)[]) => {
    setFilters(f => ({
      ...f,
      search,
      searchKey: (searchKey ?? f.searchKey) as keyof T,
    }));
    setPagination(p => ({ ...p, pageIndex: 0 }));
  };

  type Singular<T> = T extends readonly (infer U)[] ? U : T;
  const toggleCheckboxFilter = <K extends keyof T>(
    field: K,
    value: Singular<T[K]>,
  ) => {
    setFilters(f => {
      const current = (f.in[field] as T[K][] | undefined) ?? [];
      const exists = current.some(v => Object.is(v, value));
      const next = exists
        ? current.filter(v => !Object.is(v, value))
        : [...current, value];
      return { ...f, in: { ...f.in, [field]: next as unknown[] } };
    });
    setPagination(p => ({ ...p, pageIndex: 0 }));
  };

  const resetAllCheckboxFilters = () => {
    setFilters(f => ({ ...f, in: {} }));
    setPagination(p => ({ ...p, pageIndex: 0 }));
  };

  const resetCheckboxByValue = <K extends keyof T>(value: Singular<T[K]>) => {
    setFilters(f => {
      const entries = Object.entries(f.in).map(([k, arr = []]) => {
        const nextArr = (arr as unknown[]).filter(v => !Object.is(v, value));
        return [k, nextArr];
      });

      const pruned = entries.filter(([, arr]) => (arr as unknown[]).length > 0);

      return {
        ...f,
        in: Object.fromEntries(pruned) as Partial<Record<keyof T, string[]>>,
      };
    });

    setPagination(p => ({ ...p, pageIndex: 0 }));
  };

  const resetSearch = () => {
    setFilters(f => ({
      ...f,
      search: '',
      searchKey: undefined,
    }));
    setPagination(p => ({ ...p, pageIndex: 0 }));
  };

  const resetAll = () => {
    resetFilters();
    resetSort();
    resetPagination();
    resetSearch();
  };

  return [
    {
      display,
      filters,
      sort,
      pagination,
    },
    {
      resetAll,
      resetSearch,
      resetAllCheckboxFilters,
      resetCheckboxByValue,
      toggleCheckboxFilter,
      setSearch,
      showMore,
      resetFilters,
      resetSort,
      resetPagination,
      setFilters,
      setSort,
      setPagination,
    },
  ] as const;
};

export const useSetDataView = (...options: Parameters<typeof useDataView>) => {
  const [, setDataView] = useDataView(...options);
  return setDataView;
};

export const useDataViewValue = (
  ...options: Parameters<typeof useDataView>
) => {
  const [values] = useDataView(...options);
  return values;
};
